import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DynamicTable from '../../../components/lab-dynamic-table';
import DeleteConfirmationModal from '../../../components/Modals/delete-modal';
import { ProcedureDepartment } from '../../../enums/ProcedureDepartment';

interface ProcedureData {
  _id?: string;
  procedureName: string;
  procedureType: string;
  description: string;
  maxAmount: number | string;
  minAmount: number | string;
}

const Procedure: React.FC = () => {
  const [editData, setEditData] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [loading, setLoading] = useState(false);
  const [procedureData, setProcedureData] = useState<ProcedureData[]>([]);
  const [errors, setErrors] = useState({ procedureName: false });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<ProcedureData>({
    procedureName: '',
    procedureType: ProcedureDepartment.GENERAL_SURGERY,
    description: '',
    maxAmount: '',
    minAmount: '',
  });

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_IPD_API_URL}/ipd-procedure/getAllByPagination`,
        {
          params: {
            pageNo: page + 1,
            count: rowsPerPage,
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setProcedureData(response.data?.data || []);
      setTotal(response.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error fetching procedures');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (id: string, rowData: ProcedureData) => {
    setEditData(id);
    setFormData({
      procedureName: rowData.procedureName || '',
      procedureType: rowData.procedureType || ProcedureDepartment.GENERAL_SURGERY,
      description: rowData.description || '',
      maxAmount: rowData.maxAmount || '',
      minAmount: rowData.minAmount || '',
    });
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  const handleSubmit = async () => {
    // Validate procedure name
    if (!formData.procedureName.trim()) {
      setErrors({ procedureName: true });
      toast.error('Procedure name is required');
      return;
    }

    // Clear errors if validation passes
    setErrors({ procedureName: false });

    try {
      const payload = {
        procedureName: formData.procedureName,
        procedureType: formData.procedureType,
        description: formData.description,
        maxAmount: parseFloat(formData.maxAmount as string) || 0,
        minAmount: parseFloat(formData.minAmount as string) || 0,
      };

      if (editData) {
        const response = await axios.patch(
          `${process.env.REACT_APP_IPD_API_URL}/ipd-procedure/updateById/${editData}`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(response?.data?.message || 'Failed to update procedure');
        } else {
          toast.success(response?.data?.message || 'Procedure updated successfully');
          resetForm();
          fetchData();
        }
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_IPD_API_URL}/ipd-procedure/add-ipd-procedure`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(response?.data?.message || 'Failed to create procedure');
        } else {
          toast.success(response?.data?.message || 'Procedure created successfully');
          resetForm();
          fetchData();
        }
      }
    } catch (error: any) {

      toast.error(error.response?.data?.message || 'Something went wrong');
    }
  };

  const resetForm = () => {
    setFormData({
      procedureName: '',
      procedureType: ProcedureDepartment.GENERAL_SURGERY,
      description: '',
      maxAmount: '',
      minAmount: '',
    });
    setEditData(null);
    setErrors({ procedureName: false });
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteClick = (id: string) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_IPD_API_URL}/ipd-procedure/deleteById/${selectedItemId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      toast.success(response.data.message || 'Procedure deleted successfully');
      resetForm();
      fetchData();
    } catch (error: any) {
      toast.error(error.message || 'Something went wrong');
    }
    setIsDeleteModalOpen(false);
  };

  const handleExcelFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (!['xls', 'xlsx'].includes(fileExtension || '')) {
      toast.error('Please upload a valid Excel file (.xls or .xlsx)');
      e.target.value = '';
      return;
    }

    const data = new FormData();
    data.append('file', file);

    try {
      setUploading(true);

      const res = await axios.post(
        `${process.env.REACT_APP_LAB_URL}/import-data/fileUpload`,
        data,
        {
          params: { entity: 'ipdProcedure' },
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      toast.success(res.data?.message || 'File uploaded successfully');
      fetchData();

      e.target.value = '';
    } catch (error: any) {
      console.error('❌ Error uploading excel file', error);
      toast.info(
        error.response?.data?.message ||
          error.message ||
          'Failed to upload file'
      );
      e.target.value = '';
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleFile = () => {
    const link = document.createElement('a');
    link.href = '/sample_procedures.xlsx';
    link.download = 'sample_procedures.xlsx';
    link.click();
  };

  const tableHeaders = ['SN', 'Procedure Name', 'Type', 'Description', 'Min Amount', 'Max Amount', 'Actions'];

  const tableData = procedureData.map((item, index) => {
    const actions = (
      <Box>
        <IconButton onClick={() => handleEditClick(item._id!, item)}>
          <EditIcon
            sx={{
              color: '#FFB800',
              height: 20.09,
              width: 29.09,
              '&:hover': { background: 'none', color: 'rgb(133, 96, 1)' },
            }}
          />
        </IconButton>
        <IconButton onClick={() => handleDeleteClick(item._id!)}>
          <DeleteIcon
            sx={{
              color: '#FF5454',
              height: 20.09,
              width: 29.09,
              '&:hover': { background: 'none', color: 'rgb(191, 44, 44)' },
            }}
          />
        </IconButton>
      </Box>
    );

    return [
      page * rowsPerPage + index + 1,
      item.procedureName || '--',
      item.procedureType || '--',
      item.description || '--',
      item.minAmount || 0,
      item.maxAmount || 0,
      actions,
    ];
  });

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 2,
          padding: '1px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
          }}
        >
          Procedure
        </Typography>
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={4} md={4}>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              p: 2,
              boxShadow: 1,
            }}
          >
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  className="custom-input"
                  label="Procedure Name"
                  name="procedureName"
                  value={formData.procedureName}
                  fullWidth
                  required
                  error={errors.procedureName}
                  helperText={errors.procedureName ? 'Procedure name is required' : ''}
                  onChange={(e) => {
                    handleChange(e);
                    if (errors.procedureName) {
                      setErrors({ procedureName: false });
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ backgroundColor: 'white' }}>
                    Procedure Type
                  </InputLabel>
                  <Select
                    size="small"
                    className="custom-input"
                    value={formData.procedureType}
                    name="procedureType"
                    onChange={handleChange}
                  >
                    {Object.values(ProcedureDepartment).map((dept) => (
                      <MenuItem key={dept} value={dept}>
                        {dept}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  className="custom-input"
                  label="Description"
                  name="description"
                  value={formData.description}
                  fullWidth
                  multiline
                  rows={3}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  className="custom-input"
                  size="small"
                  label="Min Amount"
                  type="number"
                  name="minAmount"
                  value={formData.minAmount}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  className="custom-input"
                  size="small"
                  label="Max Amount"
                  type="number"
                  name="maxAmount"
                  value={formData.maxAmount}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Box display="flex" gap={1} mt={2}>
              {editData && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={resetForm}
                  fullWidth
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSubmit}
                fullWidth
              >
                {editData ? 'Update' : 'Submit'}
              </Button>
          
            </Box>

            <Box sx={{ width: '100%', mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              <Box>
                <input
                  type="file"
                  accept=".xls,.xlsx"
                  id="excel-upload"
                  onChange={handleExcelFileUpload}
                  style={{ display: 'none' }}
                />
                <label htmlFor="excel-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    disabled={uploading}
                    startIcon={
                      uploading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <img
                          src="/excel-logo.png"
                          alt="Excel File"
                          style={{ width: 24, height: 24 }}
                        />
                      )
                    }
                    sx={{
                      textTransform: 'none',
                      borderRadius: '10px',
                      padding: '8px 16px',
                    }}
                  >
                    {uploading ? 'Uploading...' : 'Import Excel File (.xls / .xlsx)'}
                  </Button>
                </label>
              </Box>
              <Tooltip arrow placement="top" title="Download Excel Template">
                <IconButton
                  sx={{
                    color: 'white',
                  }}
                  onClick={downloadSampleFile}
                >
                  <img
                    src="/export-excel.png"
                    alt="Excel File"
                    style={{ width: 30, height: 30 }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={8} md={8}>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <DynamicTable
              loading={loading}
              headers={tableHeaders}
              data={tableData}
              page={page}
              rowsPerPage={rowsPerPage}
              totalRows={total}
              height={"50vh"}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        </Grid>
      </Grid>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default Procedure;
